// SCALABILITY RULE: Any new surface that wants to show pinned modules
// imports this hook. Never read localStorage "perform_pinned_modules"
// directly in a component — always through this hook.

import { useCallback, useMemo, useSyncExternalStore } from "react";
import { toast } from "sonner";
import { MODULES_CATALOG, type ModuleDefinition } from "@/constants/modulesCatalog";

const PINS_KEY = "perform_pinned_modules";
const PLACEMENT_KEY = "perform_pinned_placement";
const MAX_PINS = 5;

export type PinPlacement = "sidebar" | "header" | "both";

// ── External store for cross-component sync ──

const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getPinsSnapshot(): string {
  return localStorage.getItem(PINS_KEY) ?? "[]";
}

function getPlacementSnapshot(): string {
  return localStorage.getItem(PLACEMENT_KEY) ?? "sidebar";
}

export function usePinnedModules() {
  const pinsRaw = useSyncExternalStore(subscribe, getPinsSnapshot, () => "[]");
  const placementRaw = useSyncExternalStore(subscribe, getPlacementSnapshot, () => "sidebar");

  const pinnedIds: string[] = useMemo(() => {
    try {
      return JSON.parse(pinsRaw);
    } catch {
      return [];
    }
  }, [pinsRaw]);

  const placement = placementRaw as PinPlacement;

  const setPlacement = useCallback((p: PinPlacement) => {
    localStorage.setItem(PLACEMENT_KEY, p);
    emitChange();
  }, []);

  const pin = useCallback(
    (moduleId: string) => {
      const current: string[] = JSON.parse(localStorage.getItem(PINS_KEY) ?? "[]");
      if (current.includes(moduleId)) return;
      if (current.length >= MAX_PINS) {
        toast.error(
          "Limite de 5 favoris atteinte — désépingle un module pour en ajouter un autre"
        );
        return;
      }
      localStorage.setItem(PINS_KEY, JSON.stringify([...current, moduleId]));
      emitChange();
    },
    []
  );

  const unpin = useCallback((moduleId: string) => {
    const current: string[] = JSON.parse(localStorage.getItem(PINS_KEY) ?? "[]");
    localStorage.setItem(PINS_KEY, JSON.stringify(current.filter((id) => id !== moduleId)));
    emitChange();
  }, []);

  const isPinned = useCallback(
    (moduleId: string) => pinnedIds.includes(moduleId),
    [pinnedIds]
  );

  const pinnedModules: ModuleDefinition[] = useMemo(
    () =>
      pinnedIds
        .map((id) => MODULES_CATALOG.find((m) => m.id === id))
        .filter(Boolean) as ModuleDefinition[],
    [pinnedIds]
  );

  return {
    pinnedIds,
    placement,
    setPlacement,
    pin,
    unpin,
    isPinned,
    pinnedModules,
  };
}
